import React, { useId, useState } from 'react';
import { ChevronDown, Play, Volume2, VolumeX } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type { NotificationPrefs } from '@/lib/notificationPrefs';
import type { ChimeTone } from '@/lib/chime';
import type { DesktopPermission } from '@/lib/notificationSound';

interface SoundSettingsProps {
  prefs: NotificationPrefs;
  onChange: (patch: Partial<NotificationPrefs>) => void;
  onPreview: (tone: ChimeTone) => void;
  soundBlocked: boolean;
  onEnableSound: () => void;
  desktopPermission: DesktopPermission;
  onRequestDesktop: () => Promise<boolean>;
}

function summary(prefs: NotificationPrefs): string {
  if (!prefs.soundEnabled) return 'Ton aus';
  return prefs.softSoundsEnabled ? 'Ton an · leise Töne an' : 'Ton an · leise Töne aus';
}

function SettingRow({
  id,
  label,
  hint,
  checked,
  disabled,
  onCheckedChange,
}: {
  id: string;
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <label htmlFor={id} className={cn('min-w-0 text-[13px] leading-5', disabled && 'text-muted-foreground')}>
        <span className="font-medium">{label}</span>
        {hint && <span className="block text-xs leading-4 text-muted-foreground">{hint}</span>}
      </label>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        className="mt-0.5 data-[state=checked]:bg-brand"
      />
    </div>
  );
}

/** Footer of the bell panel: a one-line summary that opens the per-browser sound settings. */
export function SoundSettings({
  prefs,
  onChange,
  onPreview,
  soundBlocked,
  onEnableSound,
  desktopPermission,
  onRequestDesktop,
}: SoundSettingsProps) {
  const [open, setOpen] = useState(false);
  const baseId = useId();
  const panelId = `${baseId}-panel`;
  const volumeId = `${baseId}-volume`;

  const toggleDesktop = async (checked: boolean) => {
    if (!checked) {
      onChange({ desktopEnabled: false });
      return;
    }
    if (desktopPermission === 'granted') onChange({ desktopEnabled: true });
    else await onRequestDesktop();
  };

  return (
    <div className="border-t border-border/70 px-2.5 pb-2 pt-1.5">
      {soundBlocked && prefs.soundEnabled && (
        <button
          type="button"
          onClick={onEnableSound}
          className="mb-1.5 flex w-full items-center gap-2 rounded-lg bg-brand-soft px-2.5 py-2 text-left text-xs font-medium text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
        >
          <VolumeX className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Der Browser hat den Ton blockiert – hier klicken, um ihn zu aktivieren.
        </button>
      )}
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-lg px-1 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
      >
        <span className="flex items-center gap-1.5">
          {prefs.soundEnabled ? (
            <Volume2 className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <VolumeX className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {summary(prefs)}
        </span>
        <span className="flex items-center gap-1 font-medium">
          Ton-Einstellungen
          <ChevronDown
            className={cn('h-3.5 w-3.5 transition-transform motion-reduce:transition-none', open && 'rotate-180')}
            aria-hidden="true"
          />
        </span>
      </button>

      <div id={panelId} hidden={!open} className="mt-1 space-y-0.5 rounded-xl bg-muted/50 px-3 py-2">
        <SettingRow
          id={`${baseId}-sound`}
          label="Ton"
          checked={prefs.soundEnabled}
          onCheckedChange={(checked) => onChange({ soundEnabled: checked })}
        />
        <SettingRow
          id={`${baseId}-soft`}
          label="Leise Töne"
          hint="Kunde schreibt mit dem Agenten, Anruf läuft"
          checked={prefs.softSoundsEnabled}
          disabled={!prefs.soundEnabled}
          onCheckedChange={(checked) => onChange({ softSoundsEnabled: checked })}
        />
        <div className="py-1.5">
          <div className="flex items-center justify-between">
            <label htmlFor={volumeId} className="text-[13px] font-medium">
              Lautstärke
            </label>
            <span className="text-xs tabular-nums text-muted-foreground">{Math.round(prefs.volume * 100)} %</span>
          </div>
          <input
            id={volumeId}
            type="range"
            min={0}
            max={100}
            step={5}
            value={Math.round(prefs.volume * 100)}
            disabled={!prefs.soundEnabled}
            onChange={(e) => onChange({ volume: Number(e.target.value) / 100 })}
            className="mt-1 w-full accent-brand disabled:opacity-50"
          />
          <div className="mt-1 flex flex-wrap gap-1.5">
            {(['urgent', 'soft'] as const).map((tone) => (
              <button
                key={tone}
                type="button"
                onClick={() => onPreview(tone)}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-medium transition-colors hover:border-brand/50 hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
              >
                <Play className="h-3 w-3" aria-hidden="true" />
                {tone === 'urgent' ? 'Dringend probehören' : 'Leise probehören'}
              </button>
            ))}
          </div>
        </div>
        {desktopPermission !== 'unsupported' && (
          <SettingRow
            id={`${baseId}-desktop`}
            label="Desktop-Benachrichtigungen"
            hint={
              desktopPermission === 'denied'
                ? 'Im Browser blockiert – in den Website-Einstellungen erlauben.'
                : 'Nur wenn dieser Tab im Hintergrund ist.'
            }
            checked={prefs.desktopEnabled && desktopPermission === 'granted'}
            disabled={desktopPermission === 'denied'}
            onCheckedChange={(checked) => void toggleDesktop(checked)}
          />
        )}
        <p className="pt-1 text-[11px] leading-4 text-muted-foreground">
          Gilt nur für diesen Browser. Bei mehreren offenen Tabs klingelt nur einer.
        </p>
      </div>
    </div>
  );
}

export default SoundSettings;
